import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * ChildProfileDrawer — Unified read-only child profile view for staff.
 *
 * Slides in from the right and aggregates everything staff typically need
 * to know about a child in one place:
 *
 *   - Overview tab: photo, age, year group, school, key safety badges
 *     (allergies / SEND / medical), parents' contact, authorised collectors
 *   - Attendance tab: last 30 days of attendance records + counts
 *   - Moments tab: recent moments shared by other staff
 *   - Reports tab: recent learning reports
 *   - Milestones tab: recent milestones achieved
 *   - Incidents tab: recent incidents
 *
 * Triggered from:
 *   - StaffDashboard → 👶 Children list (primary entry)
 *   - Daily Roster (planned future hook)
 *
 * Props:
 *   open        – boolean
 *   childId     – id of the child whose profile to display
 *   onClose     – callback to close the drawer
 */
export function ChildProfileDrawer({ open, childId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchProfile = useCallback(async () => {
    if (!childId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.staffGetChildProfile(childId, { attendance_days: 30 });
      setProfile(data);
    } catch (err) {
      setError(err.message || 'Failed to load child profile');
      toast.error(err.message || 'Failed to load child profile');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (open && childId) {
      setActiveTab('overview');
      fetchProfile();
    }
  }, [open, childId, fetchProfile]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const tabCounts = useMemo(() => ({
    moments: profile?.moments?.length ?? 0,
    reports: profile?.learning_reports?.length ?? 0,
    milestones: profile?.milestones?.length ?? 0,
    incidents: profile?.incidents?.length ?? 0,
  }), [profile]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="child-profile-drawer"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 200,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        style={{
          width: 'min(640px, 96vw)',
          height: '100vh',
          background: 'hsl(var(--background))',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.85rem',
            padding: '1rem 1.25rem',
            borderBottom: '1px solid hsl(var(--border))',
            flex: '0 0 auto',
          }}
        >
          <Avatar src={profile?.basics?.photo_url} fallback={profile?.basics?.full_name} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }} data-testid="child-profile-name">
              {loading ? 'Loading…' : (profile?.basics?.full_name || 'Child Profile')}
            </h2>
            <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
              {profile?.basics && [
                profile.basics.age_years != null && `${profile.basics.age_years}y${profile.basics.age_months || 0}m`,
                profile.basics.year_group,
                profile.basics.school_name,
              ].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost"
            data-testid="child-profile-close"
            aria-label="Close profile drawer"
            style={{ flex: '0 0 auto', padding: '0.4rem 0.7rem', fontSize: '1rem' }}
          >
            ✕
          </button>
        </div>

        {/* Tab strip */}
        <div
          style={{
            display: 'flex',
            gap: '0.25rem',
            padding: '0 1.25rem',
            borderBottom: '1px solid hsl(var(--border))',
            background: 'hsl(var(--muted) / 0.3)',
            overflowX: 'auto',
            flex: '0 0 auto',
          }}
        >
          {[
            { v: 'overview',   label: '🪪 Overview' },
            { v: 'attendance', label: `📅 Attendance` },
            { v: 'moments',    label: `📷 Moments${tabCounts.moments ? ` (${tabCounts.moments})` : ''}` },
            { v: 'reports',    label: `📊 Reports${tabCounts.reports ? ` (${tabCounts.reports})` : ''}` },
            { v: 'milestones', label: `🌱 Milestones${tabCounts.milestones ? ` (${tabCounts.milestones})` : ''}` },
            { v: 'incidents',  label: `⚠️ Incidents${tabCounts.incidents ? ` (${tabCounts.incidents})` : ''}` },
          ].map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => setActiveTab(t.v)}
              data-testid={`child-profile-tab-${t.v}`}
              style={{
                padding: '0.6rem 0.85rem',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === t.v ? '3px solid hsl(var(--primary))' : '3px solid transparent',
                color: activeTab === t.v ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                fontWeight: activeTab === t.v ? 600 : 500,
                cursor: 'pointer',
                marginBottom: '-1px',
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem', flex: '1 1 auto', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
              Loading…
            </div>
          ) : error ? (
            <div className="card" style={{ borderLeft: '4px solid hsl(0 60% 50%)', color: 'hsl(0 60% 40%)' }} data-testid="child-profile-error">
              {error}
            </div>
          ) : !profile ? null : (
            <>
              {activeTab === 'overview'   && <Overview profile={profile} />}
              {activeTab === 'attendance' && <AttendanceTab attendance={profile.attendance} />}
              {activeTab === 'moments'    && <ListTab items={profile.moments} kind="moments" />}
              {activeTab === 'reports'    && <ListTab items={profile.learning_reports} kind="reports" />}
              {activeTab === 'milestones' && <ListTab items={profile.milestones} kind="milestones" />}
              {activeTab === 'incidents'  && <ListTab items={profile.incidents} kind="incidents" />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Sub-components ----------------

function Avatar({ src, fallback }) {
  const initials = (fallback || '')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0].toUpperCase()).join('') || '👶';
  return (
    <div
      data-testid="child-profile-avatar"
      style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        overflow: 'hidden',
        background: 'hsl(var(--muted))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto',
        border: '1px solid hsl(var(--border))',
        fontSize: '1.15rem',
        fontWeight: 600,
        color: 'hsl(var(--muted-foreground))',
      }}
    >
      {src ? (
        <img src={src} alt={fallback || 'Child'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : initials}
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }} data-testid={testid}>
      <h4 style={{ margin: '0 0 0.6rem 0', fontSize: '0.95rem' }}>{title}</h4>
      {children}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div style={{ display: 'flex', gap: '0.6rem', padding: '0.25rem 0', fontSize: '0.9rem' }}>
      <span style={{ width: 130, color: 'hsl(var(--muted-foreground))', flex: '0 0 auto' }}>{label}</span>
      <span style={{ flex: 1, wordBreak: 'break-word' }} data-testid={testid}>{value || '—'}</span>
    </div>
  );
}

function Pill({ label, bg, fg, testid }) {
  return (
    <span
      data-testid={testid}
      style={{
        display: 'inline-block',
        padding: '0.2rem 0.55rem',
        background: bg,
        color: fg,
        borderRadius: 999,
        fontSize: '0.78rem',
        fontWeight: 600,
        marginRight: '0.3rem',
        marginBottom: '0.25rem',
      }}
    >
      {label}
    </span>
  );
}

function Overview({ profile }) {
  const { basics, safety, parent, authorised_collectors: collectors } = profile;
  return (
    <div data-testid="child-profile-overview">
      {/* Safety banner — high-impact info at the top */}
      {(safety.allergies?.length || safety.send_status || safety.medical_info) && (
        <div
          className="card"
          style={{
            borderLeft: '4px solid hsl(0 65% 50%)',
            background: 'hsl(0 75% 97%)',
            marginBottom: '1rem',
          }}
          data-testid="child-profile-safety-banner"
        >
          <strong style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', color: 'hsl(0 60% 35%)' }}>
            ⚠️ Safety alerts
          </strong>
          {safety.allergies?.length > 0 && (
            <div style={{ marginBottom: '0.3rem' }}>
              {safety.allergies.map((a, i) => (
                <Pill key={i} label={`🥜 ${a}`} bg="hsl(0 75% 92%)" fg="hsl(0 60% 30%)" testid={`allergy-${i}`} />
              ))}
            </div>
          )}
          {safety.send_status && (
            <Pill label="🧩 SEND" bg="hsl(260 60% 92%)" fg="hsl(260 60% 30%)" testid="send-pill" />
          )}
          {safety.medical_info && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.85rem' }}>
              <strong>Medical:</strong> {safety.medical_info}
            </div>
          )}
          {safety.send_details && (
            <div style={{ marginTop: '0.3rem', fontSize: '0.85rem' }}>
              <strong>SEND notes:</strong> {safety.send_details}
            </div>
          )}
          {safety.dietary_requirements && (
            <div style={{ marginTop: '0.3rem', fontSize: '0.85rem' }}>
              <strong>Dietary:</strong> {safety.dietary_requirements}
            </div>
          )}
        </div>
      )}

      <Section title="Child details" testid="child-section-basics">
        <Row label="Full name" value={basics.full_name} testid="basics-name" />
        <Row label="Date of birth" value={formatDOB(basics.date_of_birth)} testid="basics-dob" />
        <Row label="Age" value={basics.age_years != null ? `${basics.age_years}y ${basics.age_months || 0}m` : '—'} testid="basics-age" />
        <Row label="Year group" value={basics.year_group} testid="basics-year" />
        <Row label="Key stage" value={basics.key_stage} testid="basics-keystage" />
        <Row label="School" value={basics.school_name} testid="basics-school" />
      </Section>

      <Section title="Parent / Guardian" testid="child-section-parent">
        {parent ? (
          <>
            <Row label="Name" value={parent.full_name} testid="parent-name" />
            <Row label="Relationship" value={parent.relationship_to_child} testid="parent-relationship" />
            <Row label="Phone"
                 value={parent.phone ? <a href={`tel:${parent.phone}`}>{parent.phone}</a> : null}
                 testid="parent-phone" />
            <Row label="Email"
                 value={parent.email ? <a href={`mailto:${parent.email}`}>{parent.email}</a> : null}
                 testid="parent-email" />
            {parent.address && (
              <Row
                label="Address"
                value={[parent.address.line1, parent.address.line2, parent.address.city, parent.address.postcode].filter(Boolean).join(', ')}
                testid="parent-address"
              />
            )}
            {parent.emergency_contact && (
              <Row label="Emergency contact" value={
                typeof parent.emergency_contact === 'string'
                  ? parent.emergency_contact
                  : `${parent.emergency_contact.name || ''} ${parent.emergency_contact.phone ? '— ' + parent.emergency_contact.phone : ''}`
              } testid="parent-emergency" />
            )}
          </>
        ) : (
          <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.88rem' }}>
            No parent details recorded.
          </div>
        )}
      </Section>

      <Section title={`Authorised collectors (${collectors?.length || 0})`} testid="child-section-collectors">
        {collectors?.length ? (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {collectors.map((c, i) => (
              <li
                key={i}
                style={{ padding: '0.4rem 0', borderTop: i > 0 ? '1px solid hsl(var(--border))' : undefined }}
                data-testid={`collector-${i}`}
              >
                <strong>{c.name || c.full_name || 'Unnamed'}</strong>
                {c.relationship && <span style={{ color: 'hsl(var(--muted-foreground))' }}> · {c.relationship}</span>}
                {c.phone && <div style={{ fontSize: '0.85rem' }}><a href={`tel:${c.phone}`}>{c.phone}</a></div>}
                {(c.password || c.code) && (
                  <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                    Verification code on file
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.88rem' }}>
            No authorised collectors recorded. Only the parent may collect this child.
          </div>
        )}
      </Section>
    </div>
  );
}

function AttendanceTab({ attendance }) {
  const c = attendance?.counts || {};
  const records = attendance?.records || [];
  return (
    <div data-testid="child-profile-attendance">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: '0.5rem',
          marginBottom: '1rem',
        }}
      >
        <Stat label="Days recorded" value={c.total_records ?? 0} />
        <Stat label="Present" value={c.present ?? 0} tone="success" />
        <Stat label="Absent" value={c.absent ?? 0} tone="error" />
        <Stat label="Late" value={c.late ?? 0} tone="warning" />
      </div>

      <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
        Showing the last {attendance?.window_days || 30} days ({attendance?.from} → {attendance?.to})
      </div>

      {records.length === 0 ? (
        <div className="empty-state" style={{ padding: '1.5rem' }} data-testid="attendance-empty">
          No attendance records in this window.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }} data-testid="attendance-table">
          <thead>
            <tr>
              <th style={th}>Date</th>
              <th style={th}>Status</th>
              <th style={th}>Check-in</th>
              <th style={th}>Check-out</th>
              <th style={th}>By</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid hsl(var(--border))' }} data-testid={`attendance-row-${r.date}`}>
                <td style={td}>{r.date}</td>
                <td style={td}>{statusBadge(r.status)}</td>
                <td style={td}>{formatTime(r.check_in_time)}</td>
                <td style={td}>{formatTime(r.check_out_time)}</td>
                <td style={td} title={r.notes || ''}>
                  {r.checked_in_by || r.checked_out_by || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ListTab({ items, kind }) {
  if (!items?.length) {
    return (
      <div className="empty-state" style={{ padding: '1.5rem' }} data-testid={`${kind}-empty`}>
        No {kind} recorded yet.
      </div>
    );
  }
  return (
    <div data-testid={`${kind}-list`}>
      {items.map((it) => (
        <div
          key={it._id}
          className="card"
          style={{ marginBottom: '0.65rem' }}
          data-testid={`${kind}-item-${it._id}`}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'flex-start' }}>
            <strong>{it.title || it.type || it.domain || 'Untitled'}</strong>
            <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
              {formatDateTime(it.achieved_at || it.occurred_at || it.created_at)}
            </span>
          </div>
          {(it.summary || it.description || it.evidence) && (
            <p style={{ margin: '0.4rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--foreground))' }}>
              {it.summary || it.description || it.evidence}
            </p>
          )}
          {it.tags?.length > 0 && (
            <div style={{ marginTop: '0.4rem' }}>
              {it.tags.map((tag, i) => (
                <Pill key={i} label={`#${tag}`} bg="hsl(220 70% 92%)" fg="hsl(220 60% 30%)" />
              ))}
            </div>
          )}
          {it.severity && (
            <div style={{ marginTop: '0.3rem' }}>
              <Pill
                label={`Severity: ${it.severity}`}
                bg={it.severity === 'high' ? 'hsl(0 75% 92%)' : 'hsl(45 95% 92%)'}
                fg={it.severity === 'high' ? 'hsl(0 60% 30%)' : 'hsl(35 80% 30%)'}
              />
            </div>
          )}
          {(it.created_by_name || it.recorded_by_name || it.reported_by_name) && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
              By {it.created_by_name || it.recorded_by_name || it.reported_by_name}
            </div>
          )}
          {it.media_url && (
            <img src={it.media_url} alt="" style={{ marginTop: '0.5rem', maxWidth: '100%', borderRadius: 8 }} />
          )}
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneMap = {
    success: { bg: 'hsl(140 70% 92%)', fg: 'hsl(140 55% 26%)' },
    warning: { bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)' },
    error:   { bg: 'hsl(0 75% 94%)',  fg: 'hsl(0 60% 35%)' },
  };
  const t = toneMap[tone];
  return (
    <div style={{ padding: '0.6rem 0.8rem', borderRadius: 8, background: t ? t.bg : 'hsl(var(--muted) / 0.3)', color: t ? t.fg : 'inherit' }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.8 }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function statusBadge(status) {
  const map = {
    present:     { bg: 'hsl(140 70% 92%)', fg: 'hsl(140 55% 26%)', label: 'Present' },
    absent:      { bg: 'hsl(0 75% 94%)',   fg: 'hsl(0 60% 35%)',   label: 'Absent' },
    late:        { bg: 'hsl(45 95% 92%)',  fg: 'hsl(35 80% 30%)',  label: 'Late' },
    checked_out: { bg: 'hsl(220 70% 92%)', fg: 'hsl(220 60% 30%)', label: 'Checked out' },
    expected:    { bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 35%)', label: 'Expected' },
  };
  const m = map[status] || map.expected;
  return <Pill label={m.label} bg={m.bg} fg={m.fg} />;
}

// ---------------- Format helpers ----------------

function formatDOB(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return value; }
}

function formatDateTime(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return value; }
}

function formatTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch { return value; }
}

const th = { textAlign: 'left', padding: '0.45rem 0.5rem', fontWeight: 600, fontSize: '0.74rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase' };
const td = { padding: '0.5rem' };

export default ChildProfileDrawer;
